import { AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  getAccountUpdateWarnings,
  getMessagingLimitTier,
  getNameStatus,
  getQualityRating,
  getThroughputLevel,
  getVerifiedName,
  hasWabaStatusData,
  isDisplayNamePending,
  isDisplayNameRejected,
  isPhoneVerificationPending,
  messagingLimitTierLabel,
  nameStatusLabel,
  qualityRatingBadgeClass,
  qualityRatingLabel,
  throughputLevelLabel,
  type WhatsAppCloudConfig,
} from "@/lib/whatsapp-cloud-waba-status";

interface WhatsAppCloudWabaStatusCardProps {
  config: WhatsAppCloudConfig;
}

export function WhatsAppCloudWabaStatusCard({ config }: WhatsAppCloudWabaStatusCardProps) {
  if (!hasWabaStatusData(config)) return null;

  const qualityRating = getQualityRating(config);
  const messagingTier = getMessagingLimitTier(config);
  const throughput = getThroughputLevel(config);
  const nameStatus = getNameStatus(config);
  const verifiedName = getVerifiedName(config);
  const accountWarnings = getAccountUpdateWarnings(config);
  const phonePending = isPhoneVerificationPending(config);
  const namePending = isDisplayNamePending(config);
  const nameRejected = isDisplayNameRejected(config);

  return (
    <div className="p-4 bg-muted/30 rounded-lg space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium">Status da conta WABA</p>
        <div className="flex flex-wrap gap-1.5">
          {phonePending && (
            <Badge variant="secondary" className="text-xs">
              Verificação pendente
            </Badge>
          )}
          {namePending && (
            <Badge variant="secondary" className="text-xs">
              Nome em análise
            </Badge>
          )}
          {nameRejected && (
            <Badge variant="destructive" className="text-xs">
              Nome rejeitado
            </Badge>
          )}
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {qualityRating && (
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Qualidade do número</p>
            <Badge variant="outline" className={`text-xs ${qualityRatingBadgeClass(qualityRating)}`}>
              {qualityRatingLabel(qualityRating)}
            </Badge>
          </div>
        )}

        {messagingTier && (
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Limite de mensagens</p>
            <p className="text-sm">{messagingLimitTierLabel(messagingTier)}</p>
          </div>
        )}

        {throughput && (
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Throughput</p>
            <p className="text-sm">{throughputLevelLabel(throughput)}</p>
          </div>
        )}

        {(nameStatus || verifiedName) && (
          <div className="space-y-1 sm:col-span-2">
            <p className="text-xs text-muted-foreground">Nome de exibição</p>
            <p className="text-sm">
              {verifiedName ?? "—"}
              {nameStatus && (
                <span className="text-muted-foreground"> · {nameStatusLabel(nameStatus)}</span>
              )}
            </p>
          </div>
        )}
      </div>

      {accountWarnings.length > 0 && (
        <div className="space-y-2">
          {accountWarnings.map((warning) => (
            <div
              key={warning}
              className="flex items-start gap-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/30"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
              <p className="text-xs text-amber-700 dark:text-amber-400">{warning}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
